import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  Type,
  ComponentRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Cell Renderer Loader Component
 *
 * Dynamically loads and renders custom cell renderer components.
 * Supports both Angular component renderers and built-in text formatters.
 *
 * @example
 * ```html
 * <app-cell-renderer-loader
 *   [renderer]="statusBadgeRenderer"
 *   [params]="cellParams">
 * </app-cell-renderer-loader>
 * ```
 */
@Component({
  selector: 'app-cell-renderer-loader',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container #rendererContainer></ng-container>
    <ng-container *ngIf="!customRenderer">
      {{ formattedValue }}
    </ng-container>
  `,
})
export class CellRendererLoaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() renderer?: Type<CellRenderer<any>> | string;
  @Input() params!: CellRendererParams<any>;

  @ViewChild('rendererContainer', { read: ViewContainerRef })
  container!: ViewContainerRef;

  customRenderer: ComponentRef<CellRenderer<any>> | null = null;
  formattedValue: string = '';

  ngOnInit(): void {
    if (!this.renderer) {
      // No renderer - show raw value
      this.formattedValue = this.formatValue(this.params.value);
      return;
    }

    if (typeof this.renderer === 'string') {
      // Built-in renderer
      this.formattedValue = this.applyBuiltInRenderer(
        this.renderer,
        this.params.value,
        this.params
      );
    }
    // Component renderer will be loaded in ngAfterViewInit via container
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update params in existing custom renderer when params change
    if (changes.params && !changes.params.firstChange && this.customRenderer) {
      this.customRenderer.instance.params = this.params;
      this.customRenderer.changeDetectorRef.detectChanges();
    }

    // Update formatted value for built-in renderers
    if (changes.params && typeof this.renderer === 'string') {
      this.formattedValue = this.applyBuiltInRenderer(
        this.renderer,
        this.params.value,
        this.params
      );
    }
  }

  ngAfterViewInit(): void {
    if (this.renderer && typeof this.renderer !== 'string') {
      // Load custom component renderer
      this.loadCustomRenderer(this.renderer);
    }
  }

  ngOnDestroy(): void {
    if (this.customRenderer) {
      this.customRenderer.destroy();
    }
  }

  /**
   * Dynamically loads a custom cell renderer component
   */
  private loadCustomRenderer(
    rendererType: Type<CellRenderer<any>>
  ): void {
    if (!this.container) {
      console.error('Container not available for custom renderer');
      return;
    }

    this.container.clear();
    this.customRenderer = this.container.createComponent(rendererType);

    // Pass params to the custom renderer
    this.customRenderer.instance.params = this.params;

    // Trigger change detection
    this.customRenderer.changeDetectorRef.detectChanges();
  }

  /**
   * Applies built-in cell renderer formatting
   */
  private applyBuiltInRenderer(
    renderer: string,
    value: any,
    params: CellRendererParams<any>
  ): string {
    switch (renderer) {
      case 'text':
        return this.formatValue(value);

      case 'number':
        return this.formatNumber(value);

      case 'date':
        return this.formatDate(value);

      case 'boolean':
        return this.formatBoolean(value);

      case 'currency':
        return this.formatCurrency(value, params);

      case 'percentage':
        return this.formatPercentage(value);

      case 'badge':
        // Badge requires custom component for full styling
        return this.formatValue(value);

      case 'icon':
        // Icon requires custom component
        return '';

      case 'link':
        // Link requires custom component for href
        return this.formatValue(value);

      case 'button':
        // Button requires custom component for click handling
        return this.formatValue(value);

      case 'checkbox':
        // Checkbox requires custom component
        return this.formatBoolean(value);

      case 'image':
        // Image requires custom component for img tag
        return '';

      default:
        console.warn(`Unknown built-in renderer: ${renderer}`);
        return this.formatValue(value);
    }
  }

  /**
   * Format helpers for built-in renderers
   */
  private formatValue(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  private formatNumber(value: any): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return Number(value).toLocaleString();
  }

  private formatDate(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  }

  private formatBoolean(value: any): string {
    return value ? 'Sí' : 'No';
  }

  private formatCurrency(value: any, params: CellRendererParams<any>): string {
    if (value === null || value === undefined || isNaN(value)) return '';

    const currency =
      params.colDef?.cellRendererParams?.currency || '';
    const locale = params.colDef?.cellRendererParams?.locale || 'es-HN';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  }

  private formatPercentage(value: any): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return `${Number(value).toFixed(2)}%`;
  }
}
